import { useId, useState } from 'react';
import { ApiError } from '../../api/client';
import { useCreateInvitation } from '../../api/hooks';
import type { CreateInvitationResponse, Role } from '../../types';
import { darkTheme, FONT } from '../../theme/tokens';
import { ROLE_LABELS, grantableRoles } from './roleLabels';

const t = darkTheme;

// Minimal client-side email sanity check — the BE (`EmailStr`) is authoritative
// and rejects malformed addresses with a 422; this only stops an obviously-empty
// submit from round-tripping.
function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

interface InviteMemberFormProps {
  /** The inviter's role — bounds which roles can be granted. */
  inviterRole: Role;
  /** The most recently created invite (for the hand-carry token callout), owned
   *  by the parent so a revoke of that same invite can clear it. */
  lastCreated: CreateInvitationResponse | null;
  onCreated: (result: CreateInvitationResponse) => void;
}

// SC-2795 — invite a teammate into the caller's own org. On success the raw
// single-use token is surfaced for hand-carrying (email delivery is a stub,
// SC-2622–2627); the whole surface is gated on INVITE_MEMBER by the parent.
export function InviteMemberForm({ inviterRole, lastCreated, onCreated }: InviteMemberFormProps) {
  const roles = grantableRoles(inviterRole);
  const emailId = useId();
  const roleId = useId();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>(roles[0] ?? 'org_user');
  const create = useCreateInvitation();

  const canSubmit = looksLikeEmail(email) && !create.isPending;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    create.mutate(
      { email: email.trim(), role },
      {
        onSuccess: (data) => {
          onCreated(data);
          setEmail('');
        },
      },
    );
  };

  return (
    <section style={{ marginBottom: 28 }}>
      <h2 style={{ fontSize: 15, fontWeight: 600, color: t.text, margin: '0 0 2px' }}>
        Invite a teammate
      </h2>
      <div style={{ fontSize: 12.5, color: t.text3, marginBottom: 16 }}>
        They&rsquo;ll join your organization once they accept.
      </div>

      <form onSubmit={submit} style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 240px', minWidth: 0 }}>
          <label htmlFor={emailId} style={{ display: 'block', fontSize: 13, fontWeight: 600, color: t.text, marginBottom: 6 }}>
            Email
          </label>
          <input
            id={emailId}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="teammate@company.com"
            autoComplete="off"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '8px 12px',
              background: t.surfaceAlt,
              border: `1px solid ${t.border}`,
              borderRadius: 8,
              color: t.text,
              fontSize: 13,
              fontFamily: FONT,
            }}
          />
        </div>

        <div>
          <label htmlFor={roleId} style={{ display: 'block', fontSize: 13, fontWeight: 600, color: t.text, marginBottom: 6 }}>
            Role
          </label>
          <select
            id={roleId}
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            style={{
              padding: '8px 12px',
              background: t.surfaceAlt,
              border: `1px solid ${t.border}`,
              borderRadius: 8,
              color: t.text,
              fontSize: 13,
              fontFamily: FONT,
              cursor: 'pointer',
            }}
          >
            {roles.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABELS[r]}
              </option>
            ))}
          </select>
        </div>

        <button
          type="submit"
          disabled={!canSubmit}
          style={{
            padding: '9px 16px',
            borderRadius: 8,
            border: 'none',
            background: canSubmit ? t.accent : t.surfaceAlt,
            color: canSubmit ? t.accentButtonText : t.text3,
            fontSize: 13,
            fontWeight: 600,
            fontFamily: FONT,
            cursor: canSubmit ? 'pointer' : 'default',
          }}
        >
          {create.isPending ? 'Sending…' : 'Send invite'}
        </button>
      </form>

      {create.isError && (
        <div
          role="alert"
          style={{ marginTop: 12, fontSize: 12.5, color: t.redText }}
        >
          Couldn&rsquo;t create the invite.{' '}
          {create.error instanceof ApiError ? create.error.detail : 'Please try again.'}
        </div>
      )}

      {lastCreated && <InviteTokenCallout created={lastCreated} />}
    </section>
  );
}

// Until email delivery ships (SC-2622–2627), the org owner hand-carries the raw
// token to the invitee. We show the token itself (not a fabricated accept URL —
// the accept surface, SC-2791, doesn't exist yet) with copy-to-clipboard.
function InviteTokenCallout({ created }: { created: CreateInvitationResponse }) {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  const copy = () => {
    // The Clipboard API is absent in insecure contexts / older browsers. Don't
    // fail silently — surface it so the user knows to select the token manually
    // (the token above is selectable), since it's the only delivery path today.
    const clipboard = navigator.clipboard;
    if (!clipboard) {
      setCopyState('failed');
      return;
    }
    clipboard.writeText(created.token).then(
      () => setCopyState('copied'),
      () => setCopyState('failed'),
    );
  };

  return (
    <div
      style={{
        marginTop: 16,
        padding: '12px 14px',
        background: t.accentBg,
        border: `1px solid ${t.accentBorder}`,
        borderRadius: 8,
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, color: t.text, marginBottom: 4 }}>
        Invite created for {created.invitation.email}
      </div>
      <div style={{ fontSize: 12.5, color: t.text2, marginBottom: 10 }}>
        Email delivery isn&rsquo;t live yet — share this invite token with them to accept.
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <code
          style={{
            flex: '1 1 220px',
            minWidth: 0,
            overflowX: 'auto',
            whiteSpace: 'nowrap',
            padding: '7px 10px',
            background: t.surfaceDepressed,
            border: `1px solid ${t.border}`,
            borderRadius: 6,
            color: t.text2,
            fontSize: 12,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          }}
        >
          {created.token}
        </code>
        <button
          type="button"
          onClick={copy}
          style={{
            padding: '7px 14px',
            borderRadius: 6,
            border: `1px solid ${t.border2}`,
            background: 'transparent',
            color: t.text,
            fontSize: 12.5,
            fontWeight: 600,
            fontFamily: FONT,
            cursor: 'pointer',
          }}
        >
          {copyState === 'copied' ? 'Copied' : copyState === 'failed' ? 'Copy failed' : 'Copy'}
        </button>
      </div>
      {copyState === 'failed' && (
        <div role="status" style={{ marginTop: 8, fontSize: 12, color: t.text3 }}>
          Couldn&rsquo;t copy automatically — select the token above and copy it manually.
        </div>
      )}
    </div>
  );
}
